import { Layers, ScrollText } from 'lucide-react';
import { useSession } from '@/components/auth/session';
import { ClaudeMark } from '@/components/common/marks';
import type { AccountData } from '@/features/account/data';
import { StatTile } from '@/features/dashboard/StatTile';
import { initials } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Who the hub thinks you are, in one card rather than a header plus a panel.
 *
 * `identity` is the load-bearing half of it: signed in means the audit log names
 * an account the hub verified, and self-reported means it is repeating a string
 * this browser sent. Those are different promises and the page has to say which
 * one is in force.
 *
 * The card opens on the same themed surface the dashboard's own opening band
 * uses (`dy-surface-2`), not the fixed-dark `dy-hero` it used before (issue
 * #87): that gradient did not answer to the theme, so light mode saw a dark
 * panel here even though the rest of the page had gone light. The three
 * figures below the identity row are `StatTile`, the same primitive the
 * dashboard's own band draws with, so this card and the dashboard say a number
 * in the same shape rather than two near-copies of it.
 */
export function IdentityCard({ data }: { data: AccountData }) {
  const { session } = useSession();
  const name = session.user?.name || session.actor;
  const email = session.user?.email;
  const role = session.role;
  const admin = session.isAdmin;
  const verified = session.identity === 'session';

  return (
    <section className="dy-surface-2 px-5 py-5 sm:px-7 sm:py-6" aria-label="Your account">
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-3">
        <span
          className="grid size-14 shrink-0 place-items-center rounded-full text-lg font-semibold text-white"
          style={{ background: 'var(--dy-grad)' }}
          aria-hidden
        >
          {initials(name)}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-xl font-semibold tracking-[-0.019em]">{name}</h2>
          <p className="text-fg-2 mt-0.5 truncate text-sm">{email || 'no address on file'}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <span className="bg-raised text-fg-2 rounded-full px-2.5 py-1 text-xs font-medium">
            {admin ? 'admin' : role}
          </span>
          <span
            className={cn(
              'rounded-full px-2.5 py-1 text-xs',
              verified ? 'bg-info-bg text-info' : 'bg-warn-bg text-warn',
            )}
            title={
              verified
                ? 'Signed in with an account the hub verified; the audit log names this address, not a header.'
                : 'The admin token plus the name this browser sends. Sign in with an account and the hub names you instead.'
            }
          >
            {verified ? 'signed in' : 'self-reported identity'}
          </span>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        <StatTile
          label="Agents"
          icon={ClaudeMark}
          tone="var(--dy-info)"
          value={data.devices.length}
        />
        <StatTile
          label="Jobs opened"
          icon={ScrollText}
          tone="var(--dy-plan)"
          value={data.briefs.length}
        />
        <StatTile
          label="Tasks run"
          icon={Layers}
          tone="var(--dy-accent)"
          value={data.tasks.length}
        />
      </div>
    </section>
  );
}
